'use client'

import Link from 'next/link'
import { Card } from '@/components/ui/card'
import styles from './image-card.module.scss'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import React from 'react'

const imageCardVariants = cva(styles.card, {
  variants: {
    size: {
      default: '',
      sm: styles.cardSm,
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const containerVariants = cva('tw-flex tw-flex-col ', {
  variants: {
    size: {
      default: 'tw-space-y-2',
      sm: '',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const imageContainerVariants = cva('tw-w-full tw-h-full tw-overflow-hidden tw-rounded', {
  variants: {
    size: {
      default: 'tw-aspect-square',
      sm: 'tw-aspect-auto tw-absolute tw-top-0 tw-left-0',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const titleVariants = cva(styles.title, {
  variants: {
    size: {
      default: 'tw-text-center',
      sm: 'tw-py-2 tw-px-4',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

export type ImageCardProps = {
  title: string
  image?: string | null
  link?: string | null
  selected?: boolean
  className?: string
  onLinkClick?: () => void
} & VariantProps<typeof imageCardVariants>

const ImageCard = ({ title, image, link, size, selected, className, onLinkClick, ...props }: ImageCardProps) => {
  return (
    <Card className={cn(imageCardVariants({ size }), 'image-card tw-group', className)} {...props}>
      <Link
        href={link ?? '#'}
        className={cn(containerVariants({ size }))}
        onClick={() => {
          if (onLinkClick) {
            onLinkClick()
          }
        }}
      >
        <div
          className={cn(imageContainerVariants({ size }), {
            [styles.selected]: Boolean(selected),
          })}
        >
          <div
            className={cn(
              'tw-relative tw-z-[-1] tw-h-full tw-w-full tw-scale-100 tw-transition-transform tw-duration-500',
              {
                //'group-hover:tw-scale-110': !selected,
              }
            )}
            style={
              !!image
                ? {
                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)), url("${image}")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }
                : undefined
            }
          ></div>
        </div>
        <div
          className={cn(titleVariants({ size }), {
            [styles.selectedTitle]: selected,
            'group-hover:tw-underline': !selected,
          })}
        >
          {title ?? ''}
        </div>
      </Link>
    </Card>
  )
}

export { ImageCard, imageCardVariants }
